import { Card, Col, Flex, IconButton, Modal, Row, Text, Title } from '@wyz/ui';
import { isNil } from 'lodash-es';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans } from '~/common/context';
import {
  IAnonymizedSupplierPlatform,
  ISupplierTemplateForm,
} from '~/connect/types';

// eslint-disable-next-line @wyz/module/no-relative-import
import SupplierDraggableItem from './supplier-draggable-item';

type Props = {
  supplierIndex: number;
  suppliers: IAnonymizedSupplierPlatform[];
  onBackToRankings: () => void;
};

function BestSourcingSupplierConfiguration({
  supplierIndex,
  suppliers,
  onBackToRankings,
}: Props) {
  const methods = useFormContext<ISupplierTemplateForm>();

  const baseName =
    `suppliers.${supplierIndex}.bestSourcingConfigurations` as const;

  const usedSupplierIds =
    methods
      .watch(baseName)
      ?.flatMap((configuration) =>
        configuration.suppliers.map((brand) => brand.value),
      ) ?? [];

  const selectedSuppliers = suppliers.filter((supplier) =>
    usedSupplierIds.includes(supplier.supplierPlatform.supplier.id),
  );
  const [configuredSupplier, setConfiguredSupplier] = useState<
    IAnonymizedSupplierPlatform['supplierPlatform'] | undefined
  >();

  return (
    <>
      <Modal.Header>
        <Flex alignItems='center'>
          <IconButton
            id='back-to-rankings'
            variant='tertiary-grey'
            icon='arrow-left-s-line'
            onClick={onBackToRankings}
          >
            <Trans i18nKey='app.supplier_template.form.best_sourcing.back_to_rankings' />
          </IconButton>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <div>
          <Row>
            <Col xs={6}>
              <Card>
                <Card.Body>
                  {selectedSuppliers.length === 0 && (
                    <Text>
                      <Trans i18nKey='app.supplier_template.form.best_sourcing.no_suppliers_selected' />
                    </Text>
                  )}
                  <Flex direction='column' className='gap-2 overflow-y-auto'>
                    {selectedSuppliers.map(({ supplierPlatform }) => (
                      <Flex
                        key={supplierPlatform.supplier.id}
                        alignItems='center'
                        justifyContent='between'
                        className='badge supplier-badge'
                      >
                        <Text heading='normal' weight='semi-bold'>
                          {supplierPlatform.supplier.name}
                        </Text>
                        <IconButton
                          id={`configure-${supplierPlatform.supplier.id}`}
                          variant='tertiary-blue'
                          icon='edit'
                          onClick={() =>
                            setConfiguredSupplier(supplierPlatform)
                          }
                        >
                          <Trans i18nKey='app.common.button.configure' />
                        </IconButton>
                      </Flex>
                    ))}
                  </Flex>
                </Card.Body>
              </Card>
            </Col>
            {!isNil(configuredSupplier) && (
              <Col xs={6}>
                <Card>
                  <Card.Body>
                    <Title heading='lg' weight='bold' wrap className='mb-3'>
                      <Trans
                        i18nKey='app.supplier_template.form.suppliers.configure_supplier'
                        values={{
                          supplierName: configuredSupplier.supplier.name,
                        }}
                      />
                    </Title>
                    <SupplierDraggableItem
                      key={configuredSupplier.supplier.id}
                      baseName={`bestSourcingSupplierConfigurations.supplier${configuredSupplier.supplier.id}`}
                      supplier={{
                        id: configuredSupplier.supplier.id,
                        type: configuredSupplier.supplier.supplierType.id,
                      }}
                      index={supplierIndex}
                      display={{
                        overridePriorityIfShorterDelivery: false,
                        orderable: true,
                        childCustomerDelivery: false,
                        autoValidation: false,
                        rankingModel: true,
                        extendedManufConfiguration: true,
                      }}
                    />
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        </div>
      </Modal.Body>
    </>
  );
}

export default BestSourcingSupplierConfiguration;
